import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type AuthState } from '@/common/types/auth.types';

const baseURL = import.meta.env.VITE_API_EDMA_BASE_URL;

export type SettingsResponse = {
  pvSettingID: number;
  pvAccountID: number;
  pvSettingType: string;
  pvSettingValue: string; // JSON string of incident ID or any, e.g. "2771"
  pvVoid: number;
  pvEntryDate: string;
  pvGlobalSetting: number;
  pvGroupID: number | null;
};

export interface SettingsQueryParams {
  pvSettingID?: number;
  pvSettingType?: string;
  pvGroupID?: number;
  pvAccountID?: number;
}

export type SettingsSave = {
  pvSettingType: string;
  pvSettingValue: string;
  pvGroupID?: number | null;
  pvSettingID?: number;
  pvAccountID?: number;
};

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api/setting`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSettings: builder.query<SettingsResponse[], SettingsQueryParams>({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
        const result = await baseQuery({
          url: '',
          params: { access_token: token, ...arg },
        });
        return result as { data: SettingsResponse[] };
      },
    }),
    saveSettings: builder.mutation<string, SettingsSave>({
      query: (body) => ({
        url: body?.pvSettingID ? `/${body?.pvSettingID}` : '',
        method: body?.pvSettingID ? 'PUT' : 'POST',
        body,
      }),
    }),
  }),
});

export const { useGetSettingsQuery, useLazyGetSettingsQuery, useSaveSettingsMutation } =
  settingsApi;
